import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import type { AdminMe, AdminNotificationsInput, MailSettingsInput, MailStatus } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../../lib/api';
import { formatDate, formatDateTime, timeAgo } from '../../lib/format';
import { useAdmin, useSetAdminSession } from '../../lib/admin';
import { Alert, Button, Field, LoadingBlock } from '../../components/ui';
import { Stat } from '../../components/admin/AdminLayout';

export const mailKey = ['admin', 'mail'] as const;
export const useMailStatus = () => useQuery({ queryKey: mailKey, queryFn: () => api.get<MailStatus>('/admin/mail'), refetchInterval: 60_000 });

/** Days until the Microsoft 365 client secret expires: warn at 30 or fewer. */
export const SECRET_WARN_DAYS = 30;

/** Problems worth an admin's attention: shown on the overview and the Email page. */
export function MailWarnings({ s, linkToEmail = false }: { s: MailStatus; linkToEmail?: boolean }) {
  const more = (label: string) =>
    linkToEmail && (
      <>
        {' '}
        <Link to="/admin/email" className="font-medium underline">
          {label}
        </Link>
      </>
    );
  if (!s.enabled) {
    return (
      <Alert tone="warn">
        Email isn’t set up yet, so coach sign-ups and password resets are paused.
        {more('Set up email')}
      </Alert>
    );
  }
  const days = s.secretDaysLeft;
  // A failure that's been followed by a success has fixed itself.
  const failing = s.lastError && (!s.lastOkAt || s.lastOkAt < s.lastError.at);
  return (
    <>
      {failing && (
        <Alert>
          Emails aren’t sending. The last one failed {timeAgo(s.lastError!.at)}: {s.lastError!.message}
          {more('Check email')}
        </Alert>
      )}
      {days !== null && days <= SECRET_WARN_DAYS && (
        <Alert tone={days <= 7 ? 'danger' : 'warn'}>
          {days < 0
            ? `The Microsoft 365 client secret expired on ${formatDate(s.secretExpires!)}. Emails will fail until you add a new one.`
            : `The Microsoft 365 client secret expires ${days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`} (${formatDate(s.secretExpires!)}). Make a new one before then.`}
          {more('Check email')}
        </Alert>
      )}
    </>
  );
}

export function AdminEmailPage() {
  const qc = useQueryClient();
  const me = useAdmin();
  const q = useMailStatus();
  const [to, setTo] = useState(me.email);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const test = useMutation({
    mutationFn: () => api.post<MailStatus>('/admin/mail/test', { to }),
    onMutate: () => setSentTo(null),
    onSuccess: (s) => {
      qc.setQueryData(mailKey, s);
      setSentTo(to);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: mailKey }),
  });
  const fields = fieldErrors(test.error);

  if (q.isLoading) return <LoadingBlock />;
  if (!q.data) return <Alert>{errorMessage(q.error)}</Alert>;
  const s = q.data;
  const showForm = !s.enabled || editing;

  return (
    <>
      <h1 className="text-[24px] font-semibold tracking-[-0.01em]">Email</h1>
      <p className="mb-5 text-muted">How Hoopify sends sign-up, welcome and password emails to coaches.</p>

      <div className="mb-5 space-y-3 empty:hidden">
        <MailWarnings s={s} />
      </div>

      {showForm ? (
        <SettingsForm s={s} onDone={() => setEditing(false)} onCancel={s.enabled ? () => setEditing(false) : undefined} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Sent with" value={<span className="text-[20px]">{s.transport === 'graph' ? 'Microsoft 365' : 'SMTP'}</span>} sub={s.transport === 'graph' ? 'Microsoft Graph' : s.label} />
            <Stat label="Sent since restart" value={s.sent} sub={s.lastOkAt ? `Last one ${timeAgo(s.lastOkAt)}` : 'None yet'} />
            <Stat label="Failed since restart" value={s.failed} sub={s.lastError ? `Last one ${timeAgo(s.lastError.at)}` : 'None'} />
            <Stat
              label="Secret expires"
              value={<span className="text-[20px]">{s.secretExpires ? formatDate(s.secretExpires) : s.transport === 'graph' ? 'Not set' : '–'}</span>}
              sub={s.secretDaysLeft !== null ? (s.secretDaysLeft < 0 ? 'Expired' : `${s.secretDaysLeft} days left`) : s.transport === 'graph' ? 'Add the expiry date to get a reminder' : 'Not used with SMTP'}
            />
          </div>

          <dl className="mt-6 divide-y divide-line rounded-lg border border-line bg-surface text-[14px]">
            <div className="flex flex-wrap gap-x-4 px-4 py-3">
              <dt className="w-32 shrink-0 text-muted">From</dt>
              <dd className="min-w-0 break-all">{s.from}</dd>
            </div>
            <div className="flex flex-wrap gap-x-4 px-4 py-3">
              <dt className="w-32 shrink-0 text-muted">Replies go to</dt>
              <dd className="min-w-0 break-all">{s.replyTo ?? 'The From address'}</dd>
            </div>
            {s.lastError && (
              <div className="flex flex-wrap gap-x-4 px-4 py-3">
                <dt className="w-32 shrink-0 text-muted">Last failure</dt>
                <dd className="min-w-0">
                  “{s.lastError.subject}”, {timeAgo(s.lastError.at)}: {s.lastError.message}
                </dd>
              </div>
            )}
            {s.settings && (
              <div className="flex flex-wrap gap-x-4 px-4 py-3">
                <dt className="w-32 shrink-0 text-muted">Set up by</dt>
                <dd className="min-w-0">
                  {s.settings.savedBy}, {formatDateTime(s.settings.savedAt)}
                </dd>
              </div>
            )}
          </dl>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-muted">
            <span>Counts reset when the server restarts.</span>
            <button className="font-medium text-action hover:underline" onClick={() => setEditing(true)}>
              Change Microsoft 365 settings
            </button>
          </div>

          <section className="mt-8 max-w-md">
            <h2 className="text-[16px] font-semibold">Send a test email</h2>
            <p className="mb-3 text-[14px] text-muted">Checks that sending works end to end. It’s noted in the admin log.</p>
            <form onSubmit={(e) => { e.preventDefault(); test.mutate(); }} className="space-y-3" noValidate>
              <Field label="Send to" type="email" value={to} onChange={(e) => setTo(e.target.value)} error={fields.to} autoComplete="off" />
              {test.isError && !fields.to && <Alert>{errorMessage(test.error)}</Alert>}
              {sentTo && <Alert tone="ok">Sent to {sentTo}. If it doesn’t arrive in a minute or two, check the junk folder, then the DNS settings in the set-up guide.</Alert>}
              <Button type="submit" loading={test.isPending}>
                <Send className="size-4" aria-hidden /> Send test email
              </Button>
            </form>
          </section>
        </>
      )}

      <NotifyMe enabled={s.enabled} />
    </>
  );
}

/** Each admin's own choice: an email when a new coach signs up. */
function NotifyMe({ enabled }: { enabled: boolean }) {
  const me = useAdmin();
  const setSession = useSetAdminSession();
  const save = useMutation({
    mutationFn: (newCoaches: boolean) => api.put<AdminMe>('/admin/me/notifications', { newCoaches } satisfies AdminNotificationsInput),
    onSuccess: (admin) => setSession({ status: 'signed_in', admin }),
  });
  const checked = save.isPending ? save.variables : me.notifyNewCoaches;
  return (
    <section className="mt-10 max-w-xl border-t border-line pt-6">
      <h2 className="text-[16px] font-semibold">Emails to you</h2>
      <label className="mt-3 flex cursor-pointer items-start gap-3">
        <input type="checkbox" className="mt-1 size-4 accent-[var(--action)]" checked={checked} disabled={save.isPending} onChange={(e) => save.mutate(e.target.checked)} />
        <span>
          <span className="block text-[14px] font-medium">Email me when a new coach signs up</span>
          <span className="block text-[13px] text-muted">
            Sent to {me.email} once they’ve confirmed their email address, or when they join a team as a coach. Each admin chooses for themselves.
            {!enabled && ' It starts once email is set up.'}
          </span>
        </span>
      </label>
      {save.isError && (
        <div className="mt-3">
          <Alert>{errorMessage(save.error)}</Alert>
        </div>
      )}
    </section>
  );
}

/** Microsoft 365 settings. Saving sends a test email to you first, and only keeps them if it goes. */
function SettingsForm({ s, onDone, onCancel }: { s: MailStatus; onDone: () => void; onCancel?: () => void }) {
  const qc = useQueryClient();
  const me = useAdmin();
  const saved = s.settings;
  const [v, setV] = useState({
    tenantId: saved?.tenantId ?? '',
    clientId: saved?.clientId ?? '',
    clientSecret: '',
    sender: saved?.sender ?? 'hello@hoopify.com.au',
    replyTo: saved?.replyTo ?? '',
    secretExpires: saved?.secretExpires ?? '',
  });
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) => setV({ ...v, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => api.put<MailStatus>('/admin/mail/settings', v satisfies MailSettingsInput),
    onSuccess: (r) => {
      qc.setQueryData(mailKey, r);
      void qc.invalidateQueries({ queryKey: ['availability'] });
      onDone();
    },
  });
  const f = fieldErrors(save.error);

  return (
    <section className="max-w-xl">
      <h2 className="text-[18px] font-semibold">{saved ? 'Change Microsoft 365 settings' : 'Set up email through Microsoft 365'}</h2>
      <p className="mt-1 mb-4 text-[14px] text-muted">
        Copy these from the <strong>Hoopify mail</strong> app registration in Microsoft Entra (steps 1 to 3 of docs/email-microsoft-365.md). When you save, Hoopify sends a test email to {me.email}. It only keeps the settings if Microsoft accepts it.
      </p>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4" noValidate>
        {save.isError && !Object.keys(f).length && <Alert>{errorMessage(save.error)}</Alert>}
        <Field label="Directory (tenant) ID" value={v.tenantId} onChange={set('tenantId')} error={f.tenantId} autoComplete="off" spellCheck={false} className="field-input font-mono" />
        <Field label="Application (client) ID" value={v.clientId} onChange={set('clientId')} error={f.clientId} autoComplete="off" spellCheck={false} className="field-input font-mono" />
        <Field
          label="Client secret (its Value, not its ID)"
          type="password"
          value={v.clientSecret}
          onChange={set('clientSecret')}
          error={f.clientSecret}
          autoComplete="new-password"
          placeholder={saved ? 'Saved. Leave blank to keep it.' : ''}
          hint="Stored encrypted. It’s never shown again."
        />
        <Field label="Secret expires" type="date" value={v.secretExpires} onChange={set('secretExpires')} error={f.secretExpires} hint="From Certificates & secrets in Entra. You’ll get a reminder 30 days before." />
        <Field label="Send as" type="email" value={v.sender} onChange={set('sender')} error={f.sender} hint="The shared mailbox the app is allowed to send as." />
        <Field label="Replies go to (optional)" type="email" value={v.replyTo} onChange={set('replyTo')} error={f.replyTo} hint="Leave blank to have replies go to the Send as address." />
        <div className="flex flex-wrap gap-2">
          <Button type="submit" loading={save.isPending}>
            <Send className="size-4" aria-hidden /> Save and send a test email
          </Button>
          {onCancel && (
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
        {save.isPending && <p className="text-[13px] text-muted">Checking with Microsoft. This can take a few seconds.</p>}
      </form>
    </section>
  );
}
